<div class="setup-wizard" *ngIf="provider; else providerMissing">
  <div class="topbar">
    <button nz-button nzType="default" (click)="goBack()">{{ 'INTEGRATION.COMMON.BACK' | translate }}</button>
  </div>

  <h2>{{ 'INTEGRATION.SETUP_WIZARD.SETUP_TITLE' | translate }}: {{ provider.displayName }}</h2>

  <nz-alert
    *ngIf="existingConnection"
    nzType="info"
    nzShowIcon
    [nzMessage]="'INTEGRATION.SETUP_WIZARD.ALREADY_CONNECTED_TITLE' | translate"
    [nzDescription]="'INTEGRATION.SETUP_WIZARD.ALREADY_CONNECTED_DESC' | translate"
    class="mb-12"
  ></nz-alert>

  <nz-alert
    *ngIf="!isProviderAvailable()"
    nzType="warning"
    nzShowIcon
    [nzMessage]="'INTEGRATION.SETUP_WIZARD.COMING_SOON_TITLE' | translate"
    [nzDescription]="'INTEGRATION.SETUP_WIZARD.COMING_SOON_DESC' | translate"
    class="mb-12"
  ></nz-alert>

  <nz-steps [nzCurrent]="step">
    <nz-step [nzTitle]="'INTEGRATION.SETUP_WIZARD.STEPS.CONNECT' | translate"></nz-step>
    <nz-step [nzTitle]="'INTEGRATION.SETUP_WIZARD.STEPS.MODULES' | translate"></nz-step>
    <nz-step [nzTitle]="'INTEGRATION.SETUP_WIZARD.STEPS.SCOPE' | translate"></nz-step>
    <nz-step [nzTitle]="'INTEGRATION.SETUP_WIZARD.STEPS.SCHEDULE' | translate"></nz-step>
    <nz-step [nzTitle]="'INTEGRATION.SETUP_WIZARD.STEPS.REVIEW' | translate"></nz-step>
  </nz-steps>

  <nz-divider></nz-divider>

  <!-- STEP 0: CONNECT -->
  <div *ngIf="step === 0">
    <h3 class="step-title">{{ 'INTEGRATION.SETUP_WIZARD.STEPS.CONNECT' | translate }}</h3>

    <ng-container [ngSwitch]="provider.authType">
      <!-- OAuth -->
      <div *ngSwitchCase="'oauth2'">
        <p class="hint">{{ 'INTEGRATION.SETUP_WIZARD.OAUTH_HINT' | translate }}</p>
        <button
          nz-button
          nzType="primary"
          (click)="startOAuth()"
          [disabled]="!isProviderAvailable()"
          [nzLoading]="oauthLoading"
        >
          {{ 'INTEGRATION.SETUP_WIZARD.CONNECT_WITH' | translate:{ provider: provider.displayName } }}
        </button>

        <div class="mt-10" *ngIf="connectionId">
          <nz-alert
            nzType="success"
            nzShowIcon
            [nzMessage]="'INTEGRATION.SETUP_WIZARD.CONNECTED_TITLE' | translate"
            [nzDescription]="'INTEGRATION.SETUP_WIZARD.OAUTH_READY_DESC' | translate"
          ></nz-alert>
          <div class="mt-10">
            <button nz-button nzType="default" (click)="testConnection()" [nzLoading]="testing">
              {{ 'INTEGRATION.SETUP_WIZARD.TEST_CONNECTION' | translate }}
            </button>
          </div>
        </div>
      </div>

      <!-- Manual/API key -->
      <div *ngSwitchDefault>
        <form nz-form [formGroup]="credentialsForm">
          <ng-container *ngFor="let field of provider.credentialSchema || []">
            <nz-form-item>
              <nz-form-label [nzSpan]="6">{{ field.labelKey ? (field.labelKey | translate) : field.label }}</nz-form-label>
              <nz-form-control [nzSpan]="18">
                <input
                  nz-input
                  [type]="getInputType(field)"
                  [formControlName]="field.key"
                  [placeholder]="field.placeholderKey ? (field.placeholderKey | translate) : (field.placeholder || field.default || '')"
                />
                <div class="help" *ngIf="field.helpText || field.helpTextKey">{{ field.helpTextKey ? (field.helpTextKey | translate) : field.helpText }}</div>
              </nz-form-control>
            </nz-form-item>
          </ng-container>

          <div class="actions">
            <button
              nz-button
              nzType="primary"
              type="button"
              (click)="saveCredentials()"
              [disabled]="credentialsForm.invalid || !isProviderAvailable()"
              [nzLoading]="saving"
            >
              {{ existingConnection
              ? ('INTEGRATION.SETUP_WIZARD.UPDATE_CREDENTIALS' | translate)
              : ('INTEGRATION.SETUP_WIZARD.SAVE_CREDENTIALS' | translate) }}
            </button>

            <button
              nz-button
              nzType="default"
              type="button"
              (click)="testConnection()"
              [disabled]="!connectionId"
              [nzLoading]="testing"
            >
              {{ 'INTEGRATION.SETUP_WIZARD.TEST_CONNECTION' | translate }}
            </button>
          </div>
        </form>
      </div>
    </ng-container>

    <div class="nav">
      <button nz-button nzType="primary" (click)="next()" [disabled]="!canGoNext()">
        {{ 'INTEGRATION.COMMON.NEXT' | translate }}
      </button>
    </div>
  </div>

  <!-- STEP 1: MODULES -->
  <div *ngIf="step === 1">
    <h3 class="step-title">{{ 'INTEGRATION.SETUP_WIZARD.STEPS.MODULES' | translate }}</h3>
    <p class="hint">{{ 'INTEGRATION.SETUP_WIZARD.MODULES_HINT' | translate }}</p>

    <nz-alert
      *ngIf="!(capabilities()?.length)"
      nzType="info"
      nzShowIcon
      [nzMessage]="'INTEGRATION.SETUP_WIZARD.NO_MODULE_SELECTION_TITLE' | translate"
      [nzDescription]="'INTEGRATION.SETUP_WIZARD.NO_MODULE_SELECTION_DESC' | translate"
      class="mb-12"
    ></nz-alert>

    <div class="modules-list" *ngIf="capabilities()?.length">
      <div class="module-item" *ngFor="let opt of capabilityOptions">
        <label
          nz-checkbox
          class="module-checkbox"
          [ngModel]="modulesForm.value.enabled?.includes(opt.value)"
          (ngModelChange)="toggleModule(opt.value, $event)"
        ></label>

        <div class="module-meta">
          <div class="module-title">{{ opt.label }}</div>
          <div class="module-desc" *ngIf="opt.description">{{ opt.description }}</div>
        </div>
      </div>
    </div>

    <div class="nav">
      <button nz-button nzType="default" (click)="prev()">{{ 'INTEGRATION.COMMON.BACK' | translate }}</button>
      <button nz-button nzType="primary" (click)="next()">{{ 'INTEGRATION.COMMON.NEXT' | translate }}</button>
    </div>
  </div>

  <!-- STEP 2: SCOPE -->
  <div *ngIf="step === 2">
    <h3 class="step-title">{{ 'INTEGRATION.SETUP_WIZARD.STEPS.SCOPE' | translate }}</h3>
    <p class="hint" *ngIf="provider.supportsScopes">{{ 'INTEGRATION.SETUP_WIZARD.SCOPE_HINT' | translate }}</p>

    <nz-alert
      *ngIf="!provider.supportsScopes"
      nzType="info"
      nzShowIcon
      [nzMessage]="'INTEGRATION.SETUP_WIZARD.NO_SCOPE_TITLE' | translate"
      [nzDescription]="'INTEGRATION.SETUP_WIZARD.NO_SCOPE_DESC' | translate"
      class="mb-12"
    ></nz-alert>

    <div *ngIf="provider.supportsScopes">
      <button nz-button nzType="default" (click)="loadScopes()" [disabled]="!connectionId" [nzLoading]="loadingScopes">
        {{ 'INTEGRATION.SETUP_WIZARD.LOAD_SCOPES' | translate }}
      </button>

      <div class="mt-10">
        <nz-form-item [formGroup]="scopeForm">
          <nz-form-label [nzSpan]="6">{{ 'INTEGRATION.SETUP_WIZARD.USER_IDS' | translate }}</nz-form-label>
          <nz-form-control [nzSpan]="18">
            <nz-select
              nzMode="multiple"
              formControlName="userIds"
              [nzOptions]="scopes"
              [nzPlaceHolder]="'INTEGRATION.SETUP_WIZARD.SELECT_USER_IDS_PLACEHOLDER' | translate"
            ></nz-select>
          </nz-form-control>
        </nz-form-item>
      </div>
    </div>

    <div class="nav">
      <button nz-button nzType="default" (click)="prev()">{{ 'INTEGRATION.COMMON.BACK' | translate }}</button>
      <button nz-button nzType="primary" (click)="next()">{{ 'INTEGRATION.COMMON.NEXT' | translate }}</button>
    </div>
  </div>

  <!-- STEP 3: SCHEDULE -->
  <div *ngIf="step === 3">
    <h3 class="step-title">{{ 'INTEGRATION.SETUP_WIZARD.STEPS.SCHEDULE' | translate }}</h3>
    <p class="hint">{{ 'INTEGRATION.SETUP_WIZARD.SCHEDULE_HINT' | translate }}</p>

    <nz-alert
      *ngIf="provider.supportsScheduling === false"
      nzType="info"
      nzShowIcon
      [nzMessage]="'INTEGRATION.SETUP_WIZARD.SCHEDULING_NOT_SUPPORTED_TITLE' | translate"
      [nzDescription]="'INTEGRATION.SETUP_WIZARD.SCHEDULING_NOT_SUPPORTED_DESC' | translate"
      class="mb-12"
    ></nz-alert>

    <form nz-form [formGroup]="scheduleForm" class="schedule-form">
      <nz-form-item>
        <nz-form-label [nzSpan]="6">{{ 'INTEGRATION.SETUP_WIZARD.SYNC_MODE' | translate }}</nz-form-label>
        <nz-form-control [nzSpan]="18">
          <nz-radio-group formControlName="syncMode" class="radio-row">
            <label nz-radio nzValue="manual">{{ 'INTEGRATION.SETUP_WIZARD.SYNC_MODES.MANUAL' | translate }}</label>
            <label nz-radio nzValue="hourly">{{ 'INTEGRATION.SETUP_WIZARD.SYNC_MODES.HOURLY' | translate }}</label>
            <label nz-radio nzValue="daily">{{ 'INTEGRATION.SETUP_WIZARD.SYNC_MODES.DAILY' | translate }}</label>
            <label nz-radio nzValue="weekly">{{ 'INTEGRATION.SETUP_WIZARD.SYNC_MODES.WEEKLY' | translate }}</label>
          </nz-radio-group>
          <div class="help">{{ syncModeHelp(scheduleForm.value.syncMode ?? 'manual') }}</div>
        </nz-form-control>
      </nz-form-item>

      <nz-form-item *ngIf="scheduleForm.value.syncMode === 'daily' || scheduleForm.value.syncMode === 'weekly'">
        <nz-form-label [nzSpan]="6">{{ 'INTEGRATION.SETUP_WIZARD.TIME' | translate }}</nz-form-label>
        <nz-form-control [nzSpan]="18" class="time-row">
          <input nz-input type="number" min="0" max="23" formControlName="hour" style="width: 90px;" /> :
          <input nz-input type="number" min="0" max="59" formControlName="minute" style="width: 90px;" />
          <span class="help-inline">{{ 'INTEGRATION.SETUP_WIZARD.HH_MM' | translate }}</span>
        </nz-form-control>
      </nz-form-item>

      <nz-form-item *ngIf="scheduleForm.value.syncMode === 'weekly'">
        <nz-form-label [nzSpan]="6">{{ 'INTEGRATION.SETUP_WIZARD.DAY' | translate }}</nz-form-label>
        <nz-form-control [nzSpan]="18">
          <nz-select formControlName="dayOfWeek" style="min-width: 220px;">
            <nz-option [nzValue]="1" [nzLabel]="'INTEGRATION.DAYS.MONDAY' | translate"></nz-option>
            <nz-option [nzValue]="2" [nzLabel]="'INTEGRATION.DAYS.TUESDAY' | translate"></nz-option>
            <nz-option [nzValue]="3" [nzLabel]="'INTEGRATION.DAYS.WEDNESDAY' | translate"></nz-option>
            <nz-option [nzValue]="4" [nzLabel]="'INTEGRATION.DAYS.THURSDAY' | translate"></nz-option>
            <nz-option [nzValue]="5" [nzLabel]="'INTEGRATION.DAYS.FRIDAY' | translate"></nz-option>
            <nz-option [nzValue]="6" [nzLabel]="'INTEGRATION.DAYS.SATURDAY' | translate"></nz-option>
            <nz-option [nzValue]="7" [nzLabel]="'INTEGRATION.DAYS.SUNDAY' | translate"></nz-option>
          </nz-select>
        </nz-form-control>
      </nz-form-item>

      <nz-form-item>
        <nz-form-label [nzSpan]="6">{{ 'INTEGRATION.SETUP_WIZARD.PAUSED' | translate }}</nz-form-label>
        <nz-form-control [nzSpan]="18">
          <label nz-checkbox class="paused-row" formControlName="paused">{{ 'INTEGRATION.SETUP_WIZARD.PAUSE_AUTOMATIC_SYNC' | translate }}</label>
        </nz-form-control>
      </nz-form-item>
    </form>

    <div class="nav">
      <button nz-button nzType="default" (click)="prev()">{{ 'INTEGRATION.COMMON.BACK' | translate }}</button>
      <button nz-button nzType="primary" (click)="next()">{{ 'INTEGRATION.COMMON.NEXT' | translate }}</button>
    </div>
  </div>

  <!-- STEP 4: REVIEW -->
  <div *ngIf="step === 4">
    <h3 class="step-title">{{ 'INTEGRATION.SETUP_WIZARD.STEPS.REVIEW' | translate }}</h3>

    <div class="review">
      <div><b>{{ 'INTEGRATION.SETUP_WIZARD.REVIEW.PROVIDER' | translate }}:</b> {{ provider.displayName }}</div>
      <div><b>{{ 'INTEGRATION.SETUP_WIZARD.REVIEW.MODULES' | translate }}:</b> {{ (modulesForm.value.enabled || []).join(', ') || '-' }}</div>
      <div><b>{{ 'INTEGRATION.SETUP_WIZARD.REVIEW.SCOPE_USER_IDS' | translate }}:</b> {{ (scopeForm.value.userIds || []).join(', ') || '-' }}</div>
      <div><b>{{ 'INTEGRATION.SETUP_WIZARD.REVIEW.SYNC_MODE' | translate }}:</b> {{ scheduleForm.value.syncMode }}</div>
      <div><b>{{ 'INTEGRATION.SETUP_WIZARD.REVIEW.PAUSED' | translate }}:</b> {{ scheduleForm.value.paused ? ('INTEGRATION.COMMON.YES' | translate) : ('INTEGRATION.COMMON.NO' | translate) }}</div>
    </div>

    <div class="nav">
      <button nz-button nzType="default" (click)="prev()">{{ 'INTEGRATION.COMMON.BACK' | translate }}</button>
      <button nz-button nzType="primary" (click)="saveAllSettings()" [nzLoading]="saving" [disabled]="!connectionId">
        {{ 'INTEGRATION.SETUP_WIZARD.SAVE_AND_FINISH' | translate }}
      </button>
    </div>
  </div>
</div>

<ng-template #providerMissing>
  <div class="setup-wizard">
    <h2>{{ 'INTEGRATION.SETUP_WIZARD.NOT_FOUND_TITLE' | translate }}</h2>
    <p>{{ 'INTEGRATION.SETUP_WIZARD.NOT_FOUND_DESC' | translate }}</p>
    <button nz-button nzType="default" routerLink="/company/integrations">
      {{ 'INTEGRATION.SETUP_WIZARD.BACK_TO_INTEGRATIONS' | translate }}
    </button>
  </div>
</ng-template>
